// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
// import _ from 'lodash'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  QrCodeIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'auth/AuthContext';
import { parseDate } from 'utils';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import PrintModal from 'components/modals/PrintModal';
import QRModal from 'components/modals/QRModal';
import WorkerModal from 'components/modals/WorkerModal';
import ButtonComponent from 'components/common/ButtonComponent';
import ActionCardComponent from 'components/common/ActionCardComponent';
import SectionIntro from 'components/common/SectionIntro';
import TableComponent from 'components/common/TableComponent';
import UploadWorkersModal from 'components/modals/UploadWorkersModal';

const WorkersManagement = () => {
  const { t } = useTranslation();
  const hasFetched = useRef(false);
  const { user } = useAuth();

  const [state, setState] = useState({
    searchTerm: '',
    workers: [],
    selectedWorker: null,
    selectedWorkerForQR: null,
    totals: {
      totalWorkers: 0,
      totalActive: 0,
      totalInactive: 0
    },
    isWorkerModalOpen: false,
    isQRModalOpen: false,
    isPrintModalOpen: false,
    isOpenWorkersModal: false
  });

  const getWorkers = async () => {
    // uncomment when backend returns last entity created
    // if (hasFetched.current) return
    // hasFetched.current = true

    const response = await makeRequest({
      method: 'get',
      url: `/management/workers-management/${user.farmId}/all`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    });

    if (response) {
      setState(s => ({
        ...s,
        workers: response.data
      }));
    }
  };

  const getTotals = async () => {
    const response = await makeRequest({
      method: 'get',
      url: `/management/workers-management/totals/${user.farmId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    });

    if (response) {
      setState(s => ({
        ...s,
        totals: response.data
      }));
    }
  };

  const openAddWorkerModal = () => {
    setState(s => ({
      ...s,
      selectedWorker: {},
      isWorkerModalOpen: true
    }));
  };

  const openModalForEdit = worker => {
    setState(s => ({
      ...s,
      selectedWorker: worker,
      isWorkerModalOpen: true
    }));
  };

  const closeModal = () => {
    setState(s => ({
      ...s,
      selectedWorker: null,
      isWorkerModalOpen: false
    }));
  };

  const handleSave = async workerData => {
    const isEditing = Object.keys(state.selectedWorker).length > 0;

    const response = await makeRequest({
      method: isEditing ? 'put' : 'post',
      url: isEditing
        ? `/management/workers-management/${workerData.workerId}`
        : '/management/workers-management',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      data: {
        farmId: user.farmId,
        ...workerData
      },
      alertResult: true
    });

    if (response.status === 200) {
      closeModal();
      getWorkers();
      getTotals();
    }
  };

  const handleDelete = async workerId => {
    const response = await makeRequest({
      method: 'patch',
      url: `/management/workers-management/delete-worker/${workerId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      alertResult: true
    });

    if (response.status === 200) {
      setState(s => ({
        ...s,
        selectedWorker: null,
        isWorkerModalOpen: false
      }));
      closeModal();
    }
  };

  const handleQRClick = worker => {
    setState(s => ({
      ...s,
      selectedWorkerForQR: worker,
      isQRModalOpen: true
    }));
  };

  const handlePrintClick = worker => {
    setState(s => ({
      ...s,
      selectedWorkerForQR: worker,
      isPrintModalOpen: true
    }));
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = state.workers.map(worker => ({
        ID: worker.workerId,
        Name: worker.firstName,
        'Last Name': worker.lastName,
        Status: worker.isActive ? t('common.active') : t('common.suspended'),
        'Created At': worker.createdAt
      }));

      const worksheet = XLSXUtils.json_to_sheet(dataToExport);
      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Workers');

      // Ajustar el ancho de las columnas
      const maxWidth = dataToExport.reduce((w, r) => Math.max(w, r.Name.length), 10);
      worksheet['!cols'] = [
        { wch: 10 }, // ID
        { wch: maxWidth }, // Name
        { wch: maxWidth }, // Last Name
        { wch: 15 }, // Status
        { wch: 15 }, // Phone
        { wch: 25 }, // Email
        { wch: 15 }, // Role
        { wch: 20 } // Created At
      ];

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workers_${new Date().toISOString().split('T')[0]}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      showAlert({
        text: t('export.excelSuccess'),
        icon: 'success'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showAlert({
        text: t('export.excelFailed'),
        icon: 'error'
      });
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF();

      doc.setFontSize(16);
      doc.text(t('workers.reportTitle'), 14, 15);
      doc.setFontSize(10);
      doc.text(`${t('workers.generatedOn')}: ${new Date().toLocaleDateString()}`, 14, 25);

      const tableColumn = [
        'ID',
        'Name',
        'Last Name',
        'Status',
        'Phone',
        'Email',
        'Role',
        'Created At'
      ];

      const tableRows = state.workers.map(worker => [
        worker.workerId,
        worker.firstName,
        worker.lastName,
        worker.isActive ? t('common.active') : t('common.suspended'),
        '',
        '',
        worker.createdAt
      ]);

      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      });

      doc.save(`workers_${new Date().toISOString().split('T')[0]}.pdf`);

      showAlert({
        text: t('export.pdfSuccess'),
        icon: 'success'
      });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      showAlert({
        text: t('export.pdfFailed'),
        icon: 'error'
      });
    }
  };

  const filteredWorkers = state.workers.filter(worker => {
    const fullName = `${worker?.firstName ?? ''} ${worker?.lastName ?? ''}`.toLowerCase();
    const oldId = worker.oldId ?? '';
    const status = worker.isActive
      ? t('common.active').toLowerCase()
      : t('common.suspended').toLowerCase();

    return (
      fullName.includes(state.searchTerm) ||
      status.includes(state.searchTerm) ||
      oldId.includes(state.searchTerm)
    );
  });

  const rowsFiltered = filteredWorkers.map(worker => ({
    ...worker,
    status: worker.isActive ? t('common.active') : t('common.suspended'),
    fullName: `${worker.firstName} ${worker.lastName}`
  }));

  const workersTableData = {
    columns: [
      {
        key: 'workerId',
        label: '#'
      },
      {
        key: 'fullName',
        label: t('workers.fullName')
      },
      {
        key: 'status',
        label: t('workers.status'),
        cell: status => (
          <span className={status === t('common.active') ? 'text-green-600' : 'text-red-600'}>
            {status}
          </span>
        )
      },
      {
        key: 'createdAt',
        label: t('workers.creationDate'),
        cell: createdAt => parseDate(createdAt)
      },
      {
        key: 'actions',
        label: t('common.manage'),
        cell: (value, worker) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleQRClick(worker)}
              title={t('common.qr')}
              className="p-1 rounded transition"
            >
              <QrCodeIcon className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => openModalForEdit(worker)}
              title={t('common.edit')}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDelete(worker.workerId)}
              title={t('common.delete')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: rowsFiltered
  };

  const onToggleUploadCsvModal = () => {
    setState(s => ({
      ...s,
      isOpenWorkersModal: !s.isOpenWorkersModal
    }));
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    getWorkers();
    getTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen p-6 text-slate-100">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('nav.workers')}
        description={t('pageIntro.workersDescription')}
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="workspace-summary-stat-card rounded-lg p-4 text-center">
          <h2 className="workspace-summary-stat-value text-xl font-bold mb-2">
            {state.totals.totalWorkers}
          </h2>
          <p>{t('workers.totalWorkers')}</p>
        </div>
        <div className="workspace-summary-stat-card rounded-lg p-4 text-center">
          <h2 className="workspace-summary-stat-value text-xl font-bold mb-2">
            {state.totals.totalActive}
          </h2>
          <p>{t('workers.activeWorkers')}</p>
        </div>
        <div className="workspace-summary-stat-card rounded-lg p-4 text-center">
          <h2 className="workspace-summary-stat-value text-xl font-bold mb-2">
            {state.totals.totalInactive}
          </h2>
          <p>{t('workers.suspendedWorkers')}</p>
        </div>
      </div>

      <ActionCardComponent>
        <input
          type="text"
          value={state.searchTerm}
          placeholder={t('workers.filterPlaceholder')}
          onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
          data-align="left"
        />
        <ButtonComponent onClick={() => setState(s => ({ ...s, isOpenWorkersModal: true }))}>
          <DocumentArrowUpIcon className="w-5 h-5" />
          <span className="font-medium">{t('workers.importWorkers')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={openAddWorkerModal}>
          <PlusIcon className="w-5 h-5 mr-1" />
          {t('workers.newWorker')}
        </ButtonComponent>
        <ButtonComponent onClick={handlePrintClick}>
          <QrCodeIcon className="w-5 h-5" />
          {t('workers.printCards')}
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel} className="flex items-center gap-2">
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('export.excel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF} className="flex items-center gap-2">
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('export.pdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={workersTableData} />

      <WorkerModal
        isOpen={state.isWorkerModalOpen}
        onClose={() => setState(s => ({ ...s, isWorkerModalOpen: false }))}
        worker={state.selectedWorker}
        onSave={handleSave}
      />
      <PrintModal
        isOpen={state.isPrintModalOpen}
        onClose={() => setState(s => ({ ...s, isPrintModalOpen: false }))}
        workers={state.workers}
      />
      <QRModal
        isOpen={state.isQRModalOpen}
        onClose={() => setState(s => ({ ...s, isQRModalOpen: false }))}
        worker={state.selectedWorkerForQR}
      />
      <UploadWorkersModal isOpen={state.isOpenWorkersModal} onClose={onToggleUploadCsvModal} />
    </div>
  );
};

export default WorkersManagement;
